import {
  signalStore,
  withState,
  withMethods,
  withHooks,
  patchState,
} from "@ngrx/signals";
import { inject } from "@angular/core";
import { Router, ActivatedRoute } from "@angular/router";

export interface IContextRoute {
  scrollPosition: { scrollX: number; scrollY: number };
  route: string;
  isInit?: boolean;
  data?: any;
  breadcrumb: { breadcrumbName?: string; isActive: boolean };
  initialPath?: string;
  canGoBack: boolean;
  queryParams?: { [key: string]: any };
  mode?: "browser" | "add" | "edit"; // Only these three modes are valid
}

interface RouteState {
  contexts: IContextRoute[];
}

const initialState: RouteState = {
  contexts: [],
};

/** Seeded list-view context set before navigation (consumed on route activation). */
const pendingRouteContextData = new Map<string, unknown>();

// Function to create or reactivate contexts (ahora exportada)
export function createOrReactivateContext(
  store: any,
  url: string,
  data: any,
  queryParams: any,
  router: Router,
) {
  const breadcrumbName = data["breadcrumb"];
  const initialPath = data["initialPath"];
  const mode = data["mode"];
  const initialBreadcrumbName = data["initialBreadcrumbName"];

  const contexts = store.contexts();

  // 🔸 SPECIAL RULE: If the current route equals the initialPath,
  // reset the contexts list and create only this context as initial
  if (url === initialPath) {
    const pendingData = pendingRouteContextData.get(url);
    // Check if an initial context for this route already exists
    const existingInitialContext = contexts.find(
      (c: IContextRoute) => c.route === url,
    );

    if (existingInitialContext) {
      // If it exists, keep it as the only context and mark it as active
      const updatedContext = {
        ...existingInitialContext,
        data: pendingData ?? existingInitialContext.data,
        breadcrumb: {
          breadcrumbName:
            breadcrumbName ??
            existingInitialContext.breadcrumb.breadcrumbName,
          isActive: true,
        },
        isInit: true,
        canGoBack: false,
        queryParams,
        mode: mode ?? existingInitialContext.mode,
      };
      pendingRouteContextData.delete(url);
      patchState(store, { contexts: [updatedContext] });
      sessionStorage.setItem("routeCache", JSON.stringify([updatedContext]));
    } else {
      // If it doesn't exist, create a new one
      const newContext: IContextRoute = {
        route: url,
        breadcrumb: { breadcrumbName, isActive: true },
        scrollPosition: { scrollX: 0, scrollY: 0 },
        isInit: true,
        initialPath: url,
        canGoBack: false,
        queryParams,
        mode,
        data: pendingData,
      };
      pendingRouteContextData.delete(url);
      patchState(store, { contexts: [newContext] });
      sessionStorage.setItem("routeCache", JSON.stringify([newContext]));
    }
    return;
  }

  const existingIndex = contexts.findIndex(
    (c: IContextRoute) => c.route === url,
  );

  // Deactivate all breadcrumbs
  contexts.forEach((c: IContextRoute) => (c.breadcrumb.isActive = false));

  if (existingIndex !== -1) {
    // 🔸 If it already exists, remove the ones after and reactivate
    const newList = contexts.slice(0, existingIndex + 1);
    // Keep the existing scrollPosition and data of the reactivated context
    newList[existingIndex].breadcrumb = {
      breadcrumbName:
        breadcrumbName ?? newList[existingIndex].breadcrumb.breadcrumbName,
      isActive: true,
    };
    if (initialPath) {
      newList[existingIndex].initialPath = initialPath;
    }
    if (mode) {
      newList[existingIndex].mode = mode;
    }
    // Update queryParams with the current snapshot ones
    newList[existingIndex].queryParams = queryParams;
    patchState(store, { contexts: [...newList] });
  } else {
    // 🔸 If it doesn't exist, create a new one
    const scrollPosition = { scrollX: 0, scrollY: 0 };

    // If mode is "browser" and initial path is different, create initial + current
    if (
      mode === "browser" &&
      initialPath !== contexts[0]?.initialPath &&
      initialPath !== url
    ) {
      const initialContext: IContextRoute = {
        route: initialPath,
        breadcrumb: {
          breadcrumbName: initialBreadcrumbName,
          isActive: false,
        },
        scrollPosition,
        isInit: true,
        initialPath,
        canGoBack: false,
        mode: "browser", // Initial contexts are of type browser
      };

      const newContext: IContextRoute = {
        route: url,
        breadcrumb: {
          breadcrumbName,
          isActive: true,
        },
        scrollPosition,
        isInit: false,
        initialPath,
        canGoBack: true,
        queryParams,
        mode,
      };

      patchState(store, { contexts: [initialContext, newContext] });
    } else {
      // Create normal context
      const newContext: IContextRoute = {
        route: url,
        breadcrumb: { breadcrumbName, isActive: true },
        scrollPosition,
        isInit: initialPath === url,
        initialPath: initialPath ?? "/",
        canGoBack: true,
        queryParams,
        mode,
      };

      patchState(store, { contexts: [...contexts, newContext] });
    }
  }

  // Persist in sessionStorage
  sessionStorage.setItem("routeCache", JSON.stringify(store.contexts()));
}

/**
 * Navigation stack persisted in sessionStorage ("routeCache").
 * Saves filters, pagination and row selection so a list view restores when the user returns.
 *
 * NOT for support event logging — use ViewContextHistoryStateService for that.
 */
export const RouteStore = signalStore(
  { providedIn: "root" },
  withState(initialState),

  withMethods((store) => ({
    /**
     * Crea o reactiva contexto usando un ActivatedRoute
     * Llama internamente a createOrReactivateContext
     */
    createContextFromActivatedRoute(
      activatedRoute: ActivatedRoute,
      router: Router,
    ) {
      let currentRoute = activatedRoute;
      while (currentRoute.firstChild) {
        currentRoute = currentRoute.firstChild;
      }
      const snapshot = currentRoute.snapshot;
      let url = router.url.startsWith("/") ? router.url.slice(1) : router.url;
      url = url.split("?")[0];
      const data = snapshot.data;
      const queryParams = snapshot.queryParams;
      createOrReactivateContext(store, url, data, queryParams, router);
    },
    /** 🔹 Returns the current context for the current route, or the last context if no route is provided */
    getCurrentContext(route?: string): IContextRoute | undefined {
      const contexts = store.contexts();
      if (!route) {
        return contexts[contexts.length - 1];
      }
      const normalizedRoute = route.startsWith("/") ? route.slice(1) : route;
      return [...contexts]
        .reverse()
        .find((context) => context.route === normalizedRoute);
    },

    /** 🔹 Updates the current context */
    updateCurrentContext(update: Partial<IContextRoute>) {
      const contexts = [...store.contexts()];
      const currentIndex = contexts.length - 1;
      if (currentIndex < 0) return;
      contexts[currentIndex] = { ...contexts[currentIndex], ...update };
      patchState(store, { contexts });
      sessionStorage.setItem("routeCache", JSON.stringify(contexts));
    },

    /** Seeds list-view filter/context data for a target route before navigation. */
    setContextDataForRoute(
      route: string,
      data: unknown,
      meta?: {
        breadcrumb?: string;
        initialPath?: string;
        mode?: IContextRoute["mode"];
      },
    ) {
      const normalizedRoute = route.startsWith("/") ? route.slice(1) : route;
      const contexts = [...store.contexts()];
      const existingIndex = contexts.findIndex(
        (context) => context.route === normalizedRoute,
      );

      if (existingIndex !== -1) {
        contexts[existingIndex] = {
          ...contexts[existingIndex],
          data,
          breadcrumb: {
            breadcrumbName:
              meta?.breadcrumb ??
              contexts[existingIndex].breadcrumb.breadcrumbName,
            isActive: contexts[existingIndex].breadcrumb.isActive,
          },
          initialPath:
            meta?.initialPath ?? contexts[existingIndex].initialPath,
          mode: meta?.mode ?? contexts[existingIndex].mode,
        };
      } else {
        contexts.push({
          route: normalizedRoute,
          breadcrumb: {
            breadcrumbName: meta?.breadcrumb,
            isActive: false,
          },
          scrollPosition: { scrollX: 0, scrollY: 0 },
          canGoBack: true,
          initialPath: meta?.initialPath ?? normalizedRoute,
          mode: meta?.mode,
          data,
        });
      }

      patchState(store, { contexts });
      sessionStorage.setItem("routeCache", JSON.stringify(contexts));
      pendingRouteContextData.set(normalizedRoute, data);
    },

    /** Removes cached list-view data for a route (e.g. when leaving a list via sidenav). */
    clearContextDataForRoute(route: string) {
      const normalizedRoute = route.startsWith("/") ? route.slice(1) : route;
      pendingRouteContextData.delete(normalizedRoute);
      const contexts = store
        .contexts()
        .filter((context) => context.route !== normalizedRoute);
      patchState(store, { contexts });
      sessionStorage.setItem("routeCache", JSON.stringify(contexts));
    },

    /**
     * Drops every context after `route` (keeping it), or all contexts when `route` isn't in the trail.
     * Lets a view jump to an unrelated trail instead of appending to the current one.
     */
    trimContextsAfter(route: string) {
      const normalizedRoute = route.startsWith("/") ? route.slice(1) : route;
      const contexts = store.contexts();
      const index = contexts.findIndex((c) => c.route === normalizedRoute);
      const trimmed = index === -1 ? [] : contexts.slice(0, index + 1);
      patchState(store, { contexts: trimmed });
      sessionStorage.setItem("routeCache", JSON.stringify(trimmed));
    },

    /** 🔹 Navigate back */
    backOne(router: Router, queryParams?: any, data?: any) {
      const contexts = store.contexts();
      const prev = contexts[contexts.length - 2];

      if (!prev) {
        const last = contexts[contexts.length - 1];
        router.navigate([last?.initialPath ?? "/"]);
        return;
      }

      if (data) prev.data = { ...prev.data, ...data };

      // Update the previous context's query params if passed
      if (queryParams) {
        prev.queryParams = { ...prev.queryParams, ...queryParams };
      }

      patchState(store, { contexts: [...contexts.slice(0, -1)] });

      router.navigate([prev.route], { queryParams: prev.queryParams });
    },

    /** 🔹 Determines if nested elements can be modified */
    canModifyNested(): boolean {
      const contexts = store.contexts();

      // If there are less than 2 contexts, modification is allowed
      if (contexts.length < 2) {
        return true;
      }

      // Check if the last two contexts have mode "add" or "edit"
      const lastContext = contexts[contexts.length - 1];
      const secondLastContext = contexts[contexts.length - 2];

      const isLastAddOrEdit =
        lastContext?.mode === "add" || lastContext?.mode === "edit";
      const isSecondLastAddOrEdit =
        secondLastContext?.mode === "add" || secondLastContext?.mode === "edit";

      // If the last two contexts have mode "add" or "edit", modification is not allowed
      if (isLastAddOrEdit && isSecondLastAddOrEdit) {
        return false;
      }

      return true;
    },
  })),

  withHooks((store) => {
    const router = inject(Router);

    return {
      onInit() {
        // Recover cache if it exists
        const cached = sessionStorage.getItem("routeCache");
        if (cached) {
          try {
            patchState(store, { contexts: JSON.parse(cached) });
          } catch {}
        }
      },
    };
  }),
);
